import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/context/AuthContext";
import { LEAD_SECTIONS, STATUS_TONE } from "@/lib/leadSchema";
import SocialLinks from "@/components/SocialLinks";
import { Plus, Search, UserCheck, Loader2, FileDown, Trash2, Bot, Calendar as CalendarIcon } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import LeadsAIDialerPanel from "@/pages/LeadsAIDialerPanel";

const ALL_FIELDS = LEAD_SECTIONS.flatMap((s) => s.fields).map((f) => ({ name: f.name, label: f.label }));
const ASSIGN_COLUMNS = [
  { name: "assigned_auditor", label: "Assigned Auditor" },
  { name: "assigned_email_marketing", label: "Assigned EM" },
  { name: "assigned_team_lead", label: "Assigned TL" },
  { name: "assigned_agent", label: "Assigned Agent" },
];

const QA_STATUSES = ["", "Approved", "Pending", "Rejected", "Re-audit", "Invalid-data"];
// Filter dropdown also offers "Unset" (matches leads with no qa_audit_status).
const QA_FILTER_STATUSES = ["", "Unset", "Approved", "Pending", "Rejected", "Re-audit", "Invalid-data"];

function InlineQAStatus({ lead, canEdit, onChange }) {
  const [busy, setBusy] = useState(false);
  const current = lead.qa_audit_status || "";

  if (!canEdit) {
    return current ? (
      <span className={`text-[10px] px-2 py-0.5 rounded-sm ${STATUS_TONE[current] || "badge-neutral"}`}>{current}</span>
    ) : <span className="text-muted-foreground text-xs">—</span>;
  }

  const handle = async (e) => {
    const value = e.target.value;
    setBusy(true);
    try {
      const { data } = await api.post(`/leads/${lead.id}/qa-status`, { value });
      onChange(lead.id, data.qa_audit_status || "", data.qa_name || "");
      if (value) toast.success(`Marked ${value}`);
      else toast.success("QA status cleared");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to update");
    } finally {
      setBusy(false);
    }
  };

  const tone = STATUS_TONE[current] || "";
  return (
    <div className="relative inline-flex items-center" data-testid={`qa-status-cell-${lead.id}`}>
      <select
        value={current}
        onChange={handle}
        disabled={busy}
        data-testid={`qa-status-select-${lead.id}`}
        className={`text-[11px] pl-2 pr-6 py-1 border border-input rounded-sm appearance-none cursor-pointer bg-background ${tone}`}
      >
        {QA_STATUSES.map((s) => (
          <option key={s || "__empty"} value={s}>{s || "—"}</option>
        ))}
      </select>
      {busy && <Loader2 className="absolute right-1.5 animate-spin pointer-events-none" size={11} />}
    </div>
  );
}

function InlineQAComments({ lead, canEdit, onChange }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const current = lead.qa_comments || "";

  if (!canEdit) {
    return current
      ? <span className="text-xs whitespace-pre-wrap" title={current}>{current.length > 40 ? current.slice(0, 40) + "…" : current}</span>
      : <span className="text-muted-foreground text-xs">—</span>;
  }

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/leads/${lead.id}/qa-comments`, { value: draft });
      onChange(lead.id, data.qa_comments || "");
      setOpen(false);
      toast.success("QA comment saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative inline-block" data-testid={`qa-comments-cell-${lead.id}`}>
      <button
        type="button"
        onClick={() => { setDraft(current); setOpen((o) => !o); }}
        title={current || "Add QA comment"}
        data-testid={`qa-comments-btn-${lead.id}`}
        className={`text-[11px] px-2 py-1 border border-input rounded-sm max-w-[180px] truncate text-left hover:bg-secondary/60 ${current ? "" : "text-muted-foreground italic"}`}
      >
        {current ? (current.length > 24 ? current.slice(0, 24) + "…" : current) : "+ Add"}
      </button>
      {open && (
        <div className="absolute z-30 top-full left-0 mt-1 w-64 p-2 bg-background border border-input rounded-sm shadow-lg space-y-2" data-testid={`qa-comments-popover-${lead.id}`}>
          <textarea
            autoFocus
            rows={3}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="QA comment…"
            data-testid={`qa-comments-input-${lead.id}`}
            className="w-full text-xs border border-input rounded-sm p-1.5 bg-background resize-y"
          />
          <div className="flex justify-end gap-1.5">
            <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]" onClick={() => setOpen(false)} disabled={busy} data-testid={`qa-comments-cancel-${lead.id}`}>Cancel</Button>
            <Button size="sm" className="h-6 px-2 text-[11px]" onClick={save} disabled={busy} data-testid={`qa-comments-save-${lead.id}`}>
              {busy ? <Loader2 className="animate-spin" size={11}/> : "Save"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Leads() {
  const { options, user } = useAuth();
  const canAdd = user?.role === "admin" || user?.role === "agent";
  // True base admin only — HR/TL custom roles have base=admin which gets
  // rewritten onto `user.role`, so we use the un-rewritten `role_label`
  // to keep admin-only bulk actions hidden from those roles.
  const isAdmin = (user?.role_label || user?.role) === "admin";
  const canEditQA = user?.role === "admin" || user?.role === "qa";
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [campaign, setCampaign] = useState("");
  const [qaStatus, setQaStatus] = useState("");
  const [reassignedFilter, setReassignedFilter] = useState(""); // "" | "pending" | "resubmitted"
  const [agentFilter, setAgentFilter] = useState("");
  const [agentOptions, setAgentOptions] = useState([]);
  const [dateFilter, setDateFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [users, setUsers] = useState([]);
  const [assignBusy, setAssignBusy] = useState(false);
  // Server-side pagination. Defaults to 50/page per user request, with a
  // dropdown so admins can switch to 25/100/200 if they prefer denser/
  // sparser views. Page is 1-indexed in the UI but converted to a skip on
  // the API call.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  // Per-role feature flags from /permissions/effective. Today we check:
  //   • export_leads → shows the Export xlsx button on the bulk bar.
  //   • assign_leads → shows the entire "Bulk Assign" collapsible panel
  //     and its Apply button. Backend `/leads/bulk-assign` enforces the
  //     same gate so a frontend override can't bypass it. Admins always
  //     get both, regardless of the stored flag values.
  const [canExport, setCanExport] = useState(false);
  const [canAssign, setCanAssign] = useState(isAdmin);
  const [exporting, setExporting] = useState(false);
  // Bulk-Assign panel can be collapsed to free vertical room so the table
  // can show 20+ rows. Auto-expands the moment any row is ticked.
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignDraft, setAssignDraft] = useState({
    assigned_auditor: "", assigned_email_marketing: "", assigned_team_lead: "", assigned_agent: "",
  });
  // Bulk-QA action (admin/QA only). `bulkQaValue` is the target status the
  // user picked in the dropdown; empty string is the "Clear QA status" mode.
  // `null` is the initial "nothing picked yet" state that keeps the Apply
  // button disabled so a stray click doesn't clear statuses by mistake.
  const [bulkQaValue, setBulkQaValue] = useState(null);
  const [bulkQaBusy, setBulkQaBusy] = useState(false);
  // AI Dialer panel — opens when Admin/TL clicks the "AI SDR" button next
  // to the Campaigns filter. Only meaningful when a specific campaign is
  // picked (so we know which campaign's AI config to edit).
  const [showAIDialer, setShowAIDialer] = useState(false);
  const [campaignsFull, setCampaignsFull] = useState([]);   // {id, name}
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/campaigns");
        setCampaignsFull(data || []);
      } catch { /* silent */ }
    })();
  }, []);
  const currentCampaignId = useMemo(
    () => campaignsFull.find((c) => c.name === campaign)?.id || "",
    [campaignsFull, campaign]
  );

  // Refetch the effective permissions on mount AND whenever the global
  // `permissions:updated` event fires (admin changed a role in another tab
  // or is polling here). This makes button visibility (Export / Bulk-assign)
  // self-heal without asking the user to hard-refresh.
  const refetchEffectivePerms = useCallback(async () => {
    try {
      const { data } = await api.get("/permissions/effective");
      const pp = data.page_permissions || {};
      setCanExport(isAdmin || pp.export_leads === true);
      setCanAssign(isAdmin || pp.assign_leads === true);
    } catch (e) {
      console.debug("[Leads] /permissions/effective fetch skipped:", e?.response?.status);
    }
  }, [isAdmin]);

  useEffect(() => { refetchEffectivePerms(); }, [refetchEffectivePerms]);
  useEffect(() => {
    const handler = () => { refetchEffectivePerms(); };
    window.addEventListener("permissions:updated", handler);
    return () => window.removeEventListener("permissions:updated", handler);
  }, [refetchEffectivePerms]);

  // When the user selects rows, expand the panel automatically; when they
  // clear all selections we keep the panel as-is (don't surprise-collapse).
  useEffect(() => {
    if (selected.size > 0) setAssignOpen(true);
  }, [selected.size]);

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const skip = (page - 1) * pageSize;
      const { data } = await api.get("/leads", {
        params: {
          q: q || undefined, campaign: campaign || undefined,
          qa_status: qaStatus || undefined,
          reassigned: reassignedFilter || undefined,
          agent: agentFilter || undefined,
          date: dateFilter || undefined,
          limit: pageSize, skip,
        },
      });
      setItems(data.items);
      setTotal(data.total);
    } finally { setLoading(false); }
  };

  // Refetch whenever the page / page-size / QA-status / reassigned filter changes.
  useEffect(() => { fetchLeads(); }, [page, pageSize, qaStatus, reassignedFilter, agentFilter, dateFilter]);
  // Agent-filter dropdown roster (admin/QA only — agents only see their own leads).
  useEffect(() => {
    if (user?.role !== "admin" && user?.role !== "qa") return;
    api.get("/leads/agent-options").then(({ data }) => setAgentOptions(data || [])).catch(() => {});
  }, [user?.role]);
  // Fetch user roster for the Bulk Assign dropdowns. Admins use `/users`
  // (full payload), non-admin TLs with `assign_leads` fall back to
  // `/users/picklist` (minimal payload but accessible without `admin_users`).
  const fetchUserRoster = useCallback(async () => {
    if (!isAdmin && !canAssign) { setUsers([]); return; }
    try {
      const endpoint = isAdmin ? "/users" : "/users/picklist";
      const { data } = await api.get(endpoint);
      setUsers(data);
    } catch (e) {
      console.debug("[Leads] user roster fetch skipped:", e?.response?.status);
    }
  }, [isAdmin, canAssign]);

  useEffect(() => { fetchUserRoster(); }, [fetchUserRoster]);
  useEffect(() => {
    const handler = () => { fetchUserRoster(); };
    window.addEventListener("permissions:updated", handler);
    return () => window.removeEventListener("permissions:updated", handler);
  }, [fetchUserRoster]);

  const usersByRole = useMemo(() => {
    const r = { admin: [], agent: [], qa: [], all: [] };
    for (const u of users) {
      if (r[u.role]) r[u.role].push(u);
      r.all.push(u);
    }
    return r;
  }, [users]);

  const toggleRow = (id) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    if (selected.size === items.length) setSelected(new Set());
    else setSelected(new Set(items.map((l) => l.id)));
  };
  const clearSel = () => setSelected(new Set());

  // Selected leads → .xlsx. Calls POST /api/leads/export with the
  // ticked lead_ids and saves the returned binary as a file. Backend
  // enforces `export_leads` permission AND excludes any field the
  // caller's role has marked "hidden" — we don't need to filter here.
  const exportSelected = async () => {
    const lead_ids = Array.from(selected);
    if (lead_ids.length === 0) return toast.error("Tick at least one lead to export.");
    setExporting(true);
    try {
      const res = await api.post(
        "/leads/export",
        { lead_ids },
        { responseType: "blob" },
      );
      // Prefer the server-provided filename when present, fall back to a
      // local timestamp. The server sets `Content-Disposition: attachment;
      // filename="leads-export-...xlsx"` and exposes the header via CORS.
      let filename = `leads-export-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")}.xlsx`;
      const cd = res.headers?.["content-disposition"] || res.headers?.["Content-Disposition"];
      if (cd) {
        const m = /filename="?([^"]+)"?/.exec(cd);
        if (m) filename = m[1];
      }
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${lead_ids.length} lead${lead_ids.length === 1 ? "" : "s"} to ${filename}`);
    } catch (e) {
      // Blob error responses need to be read as text before we can show
      // the actual detail string from FastAPI.
      let msg = "Export failed";
      try {
        if (e.response?.data instanceof Blob) {
          const txt = await e.response.data.text();
          try { msg = JSON.parse(txt).detail || txt || msg; } catch { msg = txt || msg; }
        } else {
          msg = e.response?.data?.detail || msg;
        }
      } catch { /* keep default */ }
      toast.error(msg);
    } finally {
      setExporting(false);
    }
  };

  const applyAssignment = async () => {
    const lead_ids = Array.from(selected);
    if (lead_ids.length === 0) return toast.error("Select at least one lead.");
    const payload = { lead_ids };
    let any = false;
    for (const k of Object.keys(assignDraft)) {
      if (assignDraft[k]) { payload[k] = assignDraft[k]; any = true; }
    }
    if (!any) return toast.error("Pick an assignee in at least one role.");
    setAssignBusy(true);
    try {
      const { data } = await api.post("/leads/bulk-assign", payload);
      toast.success(`Assigned ${data.modified} lead${data.modified === 1 ? "" : "s"}`);
      setAssignDraft({ assigned_auditor: "", assigned_email_marketing: "", assigned_team_lead: "", assigned_agent: "" });
      clearSel();
      fetchLeads();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setAssignBusy(false); }
  };

  const unassignSelected = async () => {
    const lead_ids = Array.from(selected);
    if (lead_ids.length === 0) return toast.error("Select at least one lead.");
    if (!window.confirm(`Clear the Assigned Agent field on ${lead_ids.length} lead(s)? This does NOT delete leads or reset submissions — just removes the assignment so admin/TL can pick a different agent.`)) return;
    setAssignBusy(true);
    try {
      const { data } = await api.post("/leads/bulk-unassign", { lead_ids, fields: ["assigned_agent"] });
      toast.success(`Un-assigned ${data.modified} lead${data.modified === 1 ? "" : "s"}`);
      clearSel();
      fetchLeads();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setAssignBusy(false); }
  };

  const selectQualified = () => {
    const ids = items.filter((l) => l.qa_audit_status === "Approved").map((l) => l.id);
    setSelected(new Set(ids));
    if (ids.length === 0) toast.message("No 'Approved' leads in current view.");
  };

  // Admin-only bulk delete. Confirms via window.confirm with the count and
  // (for safety on big selections) requires typing "DELETE" when > 25.
  const [deleteBusy, setDeleteBusy] = useState(false);
  const deleteSelected = async () => {
    const lead_ids = Array.from(selected);
    if (lead_ids.length === 0) return toast.error("Tick at least one lead to delete.");
    const n = lead_ids.length;
    const first = `Delete ${n} selected lead${n === 1 ? "" : "s"} permanently? This cannot be undone.`;
    if (!window.confirm(first)) return;
    if (n > 25) {
      const typed = window.prompt(`You are about to delete ${n} leads. Type DELETE to confirm.`);
      if ((typed || "").trim().toUpperCase() !== "DELETE") {
        toast.message("Bulk delete cancelled.");
        return;
      }
    }
    setDeleteBusy(true);
    try {
      const { data } = await api.post("/leads/bulk-delete", { lead_ids });
      toast.success(`Deleted ${data.deleted} lead${data.deleted === 1 ? "" : "s"}`);
      clearSel();
      fetchLeads();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Bulk delete failed");
    } finally {
      setDeleteBusy(false);
    }
  };

  // Admin/QA bulk-set QA audit status on selected rows. Wraps
  // POST /api/leads/bulk-qa-status. Backend enforces the same role
  // gate (`require_roles("admin", "qa")`) and 5000-row cap. For
  // safety we require a confirm when >25 rows are ticked.
  const applyBulkQaStatus = async () => {
    if (bulkQaValue === null) return toast.error("Pick a QA status first.");
    const lead_ids = Array.from(selected);
    if (lead_ids.length === 0) return toast.error("Tick at least one lead.");
    const label = bulkQaValue || "Cleared";
    const n = lead_ids.length;
    const verb = bulkQaValue ? `mark as "${bulkQaValue}"` : "clear the QA status on";
    if (n > 25 && !window.confirm(`You are about to ${verb} ${n} leads. Continue?`)) return;
    setBulkQaBusy(true);
    try {
      const { data } = await api.post("/leads/bulk-qa-status", { lead_ids, value: bulkQaValue });
      toast.success(`${label}: ${data.modified} lead${data.modified === 1 ? "" : "s"} updated`);
      // Optimistically update visible rows so the QA badge/dropdown reflects
      // the new status without a full refetch.
      setItems((prev) => prev.map((row) => (
        selected.has(row.id)
          ? { ...row, qa_audit_status: bulkQaValue || "", qa_name: data.qa_name || row.qa_name }
          : row
      )));
      clearSel();
      setBulkQaValue(null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Bulk QA update failed");
    } finally {
      setBulkQaBusy(false);
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Database"
        title="Leads"
        description={`${total} record${total === 1 ? "" : "s"} in the system.`}
        testid="leads-header"
        action={canAdd ? (
          <Link to="/leads/new">
            <Button data-testid="add-lead-button" className="rounded-sm bg-primary hover:bg-[#00227A] h-10">
              <Plus size={16} className="mr-1" /> Add Lead
            </Button>
          </Link>
        ) : null}
      />
      <div className="p-4 space-y-3">
        <div className="flex flex-wrap gap-2">
          <div className="relative flex-1 min-w-[280px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={14} />
            <Input
              data-testid="leads-search-input"
              value={q} onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (page === 1 ? fetchLeads() : setPage(1))}
              placeholder="Search by email, domain, name, company, lead ID…"
              className="rounded-sm h-9 pl-9 font-mono"
            />
          </div>
          <select data-testid="leads-campaign-filter" value={campaign} onChange={(e) => setCampaign(e.target.value)}
            className="rounded-sm h-9 px-3 border border-input bg-background text-sm">
            <option value="">All Campaigns</option>
            {(options?.campaigns ?? []).map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          {isAdmin && currentCampaignId && (
            <Button data-testid="leads-ai-sdr-btn" onClick={() => setShowAIDialer(true)}
                    className="rounded-sm h-9 px-3 bg-amber-500 hover:bg-amber-600 text-white flex items-center gap-1"
                    title={`Open AI SDR dialer for ${campaign}`}>
              <Bot size={14}/> AI SDR
            </Button>
          )}
          <select data-testid="leads-qa-filter" value={qaStatus} onChange={(e) => setQaStatus(e.target.value)}
            className="rounded-sm h-9 px-3 border border-input bg-background text-sm">
            {QA_FILTER_STATUSES.map((s) => <option key={s} value={s}>{s ? "QA: " + s : "All QA statuses"}</option>)}
          </select>
          {(user?.role === "admin" || user?.role === "qa") && (
            <select
              data-testid="leads-agent-filter"
              value={agentFilter}
              onChange={(e) => setAgentFilter(e.target.value)}
              className="rounded-sm h-9 px-3 border border-input bg-background text-sm max-w-[200px]"
              title="Filter leads by agent (created by, assigned to, or worked by)"
            >
              <option value="">All Agents</option>
              {agentOptions.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          )}
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                data-testid="leads-date-filter"
                className="rounded-sm h-9 px-3 font-normal text-sm flex items-center gap-2"
                title="Filter leads by date"
              >
                <CalendarIcon size={14} className="text-muted-foreground"/>
                {dateFilter ? new Date(dateFilter + "T00:00:00").toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "All Dates"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={dateFilter ? new Date(dateFilter + "T00:00:00") : undefined}
                onSelect={(d) => {
                  if (!d) { setDateFilter(""); return; }
                  const v = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
                  setDateFilter(v);
                }}
                data-testid="leads-date-calendar"
              />
              {dateFilter && (
                <div className="p-2 border-t">
                  <Button size="sm" variant="ghost" className="w-full h-7 text-xs" onClick={() => setDateFilter("")} data-testid="leads-date-clear">
                    Clear date filter
                  </Button>
                </div>
              )}
            </PopoverContent>
          </Popover>
          {(canAssign || canEditQA) && (
            <select
              data-testid="leads-reassigned-filter"
              value={reassignedFilter}
              onChange={(e) => setReassignedFilter(e.target.value)}
              className="rounded-sm h-9 px-3 border border-input bg-background text-sm"
              title="Filter reassigned leads by resubmit status"
            >
              <option value="">All Assignment states</option>
              <option value="pending">Reassigned · Pending resubmit</option>
              <option value="resubmitted">Reassigned · Resubmitted</option>
              <option value="unassigned">Unassigned only</option>
            </select>
          )}
          <Button data-testid="leads-search-button" onClick={() => (page === 1 ? fetchLeads() : setPage(1))} className="rounded-sm h-9" variant="outline">Apply</Button>
          {canExport && (
            <Button
              data-testid="leads-export-button"
              onClick={exportSelected}
              disabled={exporting || selected.size === 0}
              className="rounded-sm h-9 ml-auto bg-emerald-600 hover:bg-emerald-700 text-white"
              title={selected.size === 0 ? "Tick rows below to enable export" : `Export ${selected.size} selected lead${selected.size === 1 ? "" : "s"} to Excel`}
            >
              {exporting
                ? <Loader2 size={14} className="animate-spin mr-1" />
                : <FileDown size={14} className="mr-1" />}
              Export
              {selected.size > 0 && (
                <span className="ml-1.5 text-[11px] rounded-sm bg-emerald-800/30 px-1.5 py-0.5" data-testid="leads-export-count">
                  {selected.size}
                </span>
              )}
            </Button>
          )}
        </div>

        {canEditQA && (
          <div className="border border-border rounded-sm bg-secondary/30" data-testid="bulk-qa-bar">
            <div className="flex items-center gap-3 flex-wrap px-3 py-2">
              <span className="text-[10px] uppercase tracking-wider font-semibold flex items-center gap-1">
                <UserCheck size={12} className="text-emerald-700" /> Bulk QA Status
              </span>
              <span className={`meta-label ${selected.size === 0 ? "text-amber-700" : "text-emerald-700"}`} data-testid="bulk-qa-counter">
                {selected.size === 0 ? "Tick rows below to enable" : `${selected.size} selected`}
              </span>
              {!canAssign && (
                <Button size="sm" variant="outline" onClick={toggleAll} className="rounded-sm h-7 text-[11px]" data-testid="qa-select-all-button">
                  {selected.size === items.length && items.length > 0 ? "Clear all" : "Select all on page"}
                </Button>
              )}
              <select
                value={bulkQaValue === null ? "__none" : bulkQaValue}
                onChange={(e) => {
                  const v = e.target.value;
                  setBulkQaValue(v === "__none" ? null : v);
                }}
                data-testid="bulk-qa-status-select"
                className="rounded-sm h-7 px-2 border border-input bg-background text-[11px] font-mono"
              >
                <option value="__none">— Pick status —</option>
                <option value="Approved">Approved</option>
                <option value="Pending">Pending</option>
                <option value="Rejected">Rejected</option>
                <option value="Re-audit">Re-audit</option>
                <option value="Invalid-data">Invalid-data</option>
                <option value="">Clear (unset)</option>
              </select>
              <Button
                size="sm"
                onClick={applyBulkQaStatus}
                disabled={bulkQaBusy || selected.size === 0 || bulkQaValue === null}
                className="rounded-sm h-7 text-[11px] bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="apply-bulk-qa-button"
                title={
                  selected.size === 0
                    ? "Tick rows first"
                    : bulkQaValue === null
                      ? "Pick a QA status"
                      : `Set ${selected.size} lead${selected.size === 1 ? "" : "s"} to ${bulkQaValue || "Unset"}`
                }
              >
                {bulkQaBusy ? <Loader2 size={11} className="mr-1 animate-spin" /> : <UserCheck size={11} className="mr-1" />}
                Apply QA
                {selected.size > 0 && (
                  <span className="ml-1.5 text-[10px] rounded-sm bg-emerald-800/30 px-1.5 py-0.5" data-testid="bulk-qa-apply-count">
                    {selected.size}
                  </span>
                )}
              </Button>
              {!canAssign && selected.size > 0 && (
                <Button size="sm" variant="ghost" onClick={clearSel} className="rounded-sm h-7 text-[11px]" data-testid="qa-clear-selection">Clear</Button>
              )}
            </div>
          </div>
        )}

        {canAssign && (
          <div className="border border-border rounded-sm bg-secondary/30" data-testid="bulk-assign-bar">
            <div className="flex items-center gap-3 flex-wrap px-3 py-2">
              <button
                type="button"
                onClick={() => setAssignOpen((o) => !o)}
                data-testid="bulk-assign-toggle"
                aria-expanded={assignOpen}
                className="text-[10px] uppercase tracking-wider font-semibold flex items-center gap-1 hover:underline"
              >
                <span>{assignOpen ? "▾" : "▸"}</span>
                <span>Bulk Assign</span>
              </button>
              <span className={`meta-label ${selected.size === 0 ? "text-amber-700" : "text-emerald-700"}`} data-testid="bulk-assign-counter">
                {selected.size === 0 ? "Tick rows below to enable" : `${selected.size} selected`}
              </span>
              <Button size="sm" variant="outline" onClick={toggleAll} className="rounded-sm h-7 text-[11px]" data-testid="select-all-button">
                {selected.size === items.length && items.length > 0 ? "Clear all" : "Select all on page"}
              </Button>
              <Button size="sm" variant="outline" onClick={selectQualified} className="rounded-sm h-7 text-[11px]" data-testid="select-qualified-button">
                <UserCheck size={11} className="mr-1" /> QA-Approved
              </Button>
              {selected.size > 0 && <Button size="sm" variant="ghost" onClick={clearSel} className="rounded-sm h-7 text-[11px]">Clear</Button>}
              {/* Bulk delete stays admin-only — assign permission does NOT
                  imply the far more destructive delete permission. Only the
                  literal base admin role sees this button. */}
              {isAdmin && selected.size > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={deleteSelected}
                  disabled={deleteBusy}
                  className="rounded-sm h-7 text-[11px] ml-auto border-red-300 text-red-700 hover:bg-red-50 hover:text-red-800"
                  data-testid="bulk-delete-button"
                  title={`Delete ${selected.size} selected lead${selected.size === 1 ? "" : "s"} permanently`}
                >
                  {deleteBusy ? <Loader2 size={11} className="mr-1 animate-spin" /> : <Trash2 size={11} className="mr-1" />}
                  Delete
                  <span className="ml-1.5 text-[10px] rounded-sm bg-red-100 px-1.5 py-0.5" data-testid="bulk-delete-count">
                    {selected.size}
                  </span>
                </Button>
              )}
            </div>
            {assignOpen && (
              <div className="px-3 pb-3 pt-1 space-y-2 border-t border-border/60">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                  <AssignSelect label="Auditor (QA)" testid="assign-auditor" users={usersByRole.all} preferRole={usersByRole.qa} value={assignDraft.assigned_auditor}
                    onChange={(v) => setAssignDraft({ ...assignDraft, assigned_auditor: v })} />
                  <AssignSelect label="Email Marketing" testid="assign-email-marketing" users={usersByRole.all} value={assignDraft.assigned_email_marketing}
                    onChange={(v) => setAssignDraft({ ...assignDraft, assigned_email_marketing: v })} />
                  <AssignSelect label="Team Lead" testid="assign-team-lead" users={usersByRole.all} preferRole={usersByRole.admin} value={assignDraft.assigned_team_lead}
                    onChange={(v) => setAssignDraft({ ...assignDraft, assigned_team_lead: v })} />
                  <AssignSelect label="Agent" testid="assign-agent" users={usersByRole.all} preferRole={usersByRole.agent} value={assignDraft.assigned_agent}
                    onChange={(v) => setAssignDraft({ ...assignDraft, assigned_agent: v })} />
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Button onClick={applyAssignment} disabled={assignBusy} className="rounded-sm bg-primary hover:bg-[#00227A] h-8" data-testid="apply-assign-button">
                    {assignBusy ? <Loader2 size={14} className="animate-spin mr-1" /> : null} Apply Assignment
                  </Button>
                  <Button
                    onClick={unassignSelected}
                    disabled={assignBusy || selected.size === 0}
                    variant="outline"
                    className="rounded-sm h-8 border-amber-300 text-amber-700 hover:bg-amber-50"
                    data-testid="bulk-unassign-button"
                    title="Clear the Assigned Agent field on the selected leads (also works for already-resubmitted leads)"
                  >
                    Unassign selected
                  </Button>
                  <span className="text-[11px] text-muted-foreground">Tick rows below, pick assignee, then Apply. Or Unassign to clear.</span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Scroll container — vertical scrolls rows while header stays
            pinned at the top via `sticky top-0`. Height sized so ~20
            compact rows fit at 1080p with bulk-assign collapsed.
            `overscroll-contain` stops momentum scroll from chaining up
            to the page body — prevents the "whole UI drags left/right
            when I scroll the table" report on touch devices.
            The body has `touch-action: pan-y` to lock horizontal drag
            at the page shell (matches 6sense.com's steady-shell UX),
            so this table opts back into horizontal panning explicitly
            with `touch-action: pan-x pan-y` — otherwise finger-swipes
            on the wide column set wouldn't scroll it at all. */}
        <div
          data-testid="leads-table-scroll"
          className="border border-border overflow-auto overscroll-contain max-h-[calc(100vh-280px)]"
          style={{ touchAction: "pan-x pan-y" }}
        >
          <table className="w-full text-xs border-collapse">
            <thead className="bg-secondary/80 sticky top-0 z-20 shadow-[inset_0_-1px_0_var(--border)]">
              <tr className="text-left">
                {(isAdmin || canExport || canAssign || canEditQA) && (
                  <th className="px-2 py-1 sticky left-0 bg-secondary/95 z-30 w-8">
                    <Checkbox className="rounded-sm" checked={items.length > 0 && selected.size === items.length} onCheckedChange={toggleAll} data-testid="select-all-checkbox" />
                  </th>
                )}
                {ALL_FIELDS.map((f) => (
                  <th key={f.name} className="px-2 py-1 meta-label whitespace-nowrap">{f.label}</th>
                ))}
                {ASSIGN_COLUMNS.map((f) => (
                  <th key={f.name} className="px-2 py-1 meta-label whitespace-nowrap">{f.label}</th>
                ))}
                <th className="px-2 py-1 meta-label whitespace-nowrap" title="Agent who resubmitted this reassigned lead">Resubmitted By</th>
                <th className="px-2 py-1 meta-label whitespace-nowrap">Created</th>
                <th className="px-2 py-1 meta-label whitespace-nowrap" title="AI SDR dial count">📞 Dialed</th>
                <th className="px-2 py-1 meta-label whitespace-nowrap" title="Last AI outcome">AI Outcome</th>
                <th className="px-2 py-1 meta-label whitespace-nowrap">Survey Answers</th>
                <th className="px-2 py-1 meta-label whitespace-nowrap">Social Links</th>
                <th className="px-2 py-1 meta-label sticky right-0 bg-secondary/95 z-30"></th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={ALL_FIELDS.length + ASSIGN_COLUMNS.length + ((isAdmin || canExport || canAssign || canEditQA) ? 8 : 7)} className="px-3 py-6 text-center text-muted-foreground">Loading…</td></tr>}
              {!loading && items.length === 0 && (
                <tr><td colSpan={ALL_FIELDS.length + ASSIGN_COLUMNS.length + ((isAdmin || canExport || canAssign || canEditQA) ? 8 : 7)} className="px-3 py-12 text-center text-muted-foreground">No leads.</td></tr>
              )}
              {items.map((l) => (
                <tr key={l.id} className="border-b border-border last:border-0 hover:bg-secondary/40 leading-none" data-testid={`lead-row-${l.id}`}>
                  {(isAdmin || canExport || canAssign || canEditQA) && (
                    <td className="px-2 py-0.5 sticky left-0 bg-background z-10 hover:bg-secondary/40">
                      <Checkbox className="rounded-sm" checked={selected.has(l.id)} onCheckedChange={() => toggleRow(l.id)} data-testid={`select-lead-${l.id}`} />
                    </td>
                  )}
                  {ALL_FIELDS.map((f) => {
                    const v = l[f.name];
                    if (f.name === "qa_comments") {
                      return (
                        <td key={f.name} className="px-2 py-0.5 whitespace-nowrap">
                          <InlineQAComments
                            lead={l}
                            canEdit={canEditQA}
                            onChange={(id, newComment) =>
                              setItems((prev) => prev.map((row) =>
                                row.id === id ? { ...row, qa_comments: newComment } : row
                              ))
                            }
                          />
                        </td>
                      );
                    }
                    if (f.name === "qa_audit_status") {
                      return (
                        <td key={f.name} className="px-2 py-0.5 whitespace-nowrap">
                          <InlineQAStatus
                            lead={l}
                            canEdit={canEditQA}
                            onChange={(id, newStatus, newQaName) =>
                              setItems((prev) => prev.map((row) =>
                                row.id === id ? { ...row, qa_audit_status: newStatus, qa_name: newQaName || row.qa_name } : row
                              ))
                            }
                          />
                        </td>
                      );
                    }
                    const cls = ["work_email", "alternate_email", "domain", "website", "lead_id",
                                 "linkedin_company_link", "linkedin_prospect_link", "revenue_link",
                                 "work_phone", "direct_number", "ext", "zip_code"].includes(f.name)
                      ? "font-mono text-[11px]" : "";
                    return (
                      <td key={f.name} className={`px-2 py-0.5 whitespace-nowrap ${cls}`}>{v || ""}</td>
                    );
                  })}
                  {ASSIGN_COLUMNS.map((f) => {
                    // Show a small green "A" badge next to Assigned Agent
                    // so Admin/TL see at-a-glance that a lead is already
                    // assigned — prevents accidental double-assignment.
                    const isAssignedAgent = f.name === "assigned_agent";
                    const val = l[f.name];
                    const nameVal = l[`${f.name}_name`];
                    const display = nameVal || val || "—";
                    return (
                      <td key={f.name} className="px-2 py-0.5 whitespace-nowrap font-mono text-[10px]" data-testid={`cell-${f.name}-${l.id}`}>
                        {isAssignedAgent && val ? (
                          <span className="inline-flex items-center gap-1">
                            <span
                              className="inline-block w-4 h-4 rounded-sm bg-emerald-600 text-white text-[9px] font-bold flex items-center justify-center"
                              title="Already assigned to an agent — do not reassign"
                              data-testid={`assigned-badge-${l.id}`}
                            >A</span>
                            <span>{display}</span>
                          </span>
                        ) : (display)}
                      </td>
                    );
                  })}
                  <td className="px-2 py-0.5 whitespace-nowrap" data-testid={`cell-resubmitted-by-${l.id}`}>
                    {l.resubmitted_by_name ? (
                      <span className="inline-flex items-center gap-1 text-[10px]">
                        <span className="font-semibold text-sky-800">{l.resubmitted_by_name}</span>
                        {l.resubmission_date && (
                          <span className="font-mono text-muted-foreground">· {l.resubmission_date}</span>
                        )}
                      </span>
                    ) : <span className="text-[10px] text-muted-foreground">—</span>}
                  </td>
                  <td className="px-2 py-0.5 font-mono text-[10px] text-muted-foreground whitespace-nowrap">{(l.created_at || "").slice(0, 10)}</td>
                  <td className="px-2 py-0.5 whitespace-nowrap text-center">
                    {l.ai_call_count ? (
                      <span className="text-[10px] font-mono font-bold text-slate-700">
                        {l.ai_call_count}
                      </span>
                    ) : <span className="text-[10px] text-muted-foreground">—</span>}
                  </td>
                  <td className="px-2 py-0.5 whitespace-nowrap">
                    {l.ai_last_outcome ? (
                      <span className={
                        "text-[10px] font-semibold px-1.5 py-0.5 rounded " +
                        ({
                          hot_lead: "bg-red-100 text-red-800",
                          warm_lead: "bg-orange-100 text-orange-800",
                          meeting_booked: "bg-emerald-100 text-emerald-800",
                          callback_requested: "bg-blue-100 text-blue-800",
                          follow_up_needed: "bg-yellow-100 text-yellow-800",
                          not_interested: "bg-slate-200 text-slate-600",
                          do_not_call: "bg-red-200 text-red-900",
                          called_no_answer: "bg-slate-100 text-slate-500",
                          called_voicemail: "bg-slate-100 text-slate-500",
                          in_progress: "bg-blue-50 text-blue-600 animate-pulse",
                        }[l.ai_last_outcome] || "bg-slate-100 text-slate-500")
                      }>
                        {l.ai_last_outcome.replaceAll("_", " ")}
                      </span>
                    ) : <span className="text-[10px] text-muted-foreground">—</span>}
                  </td>
                  <td className="px-2 py-0.5 text-[10px] max-w-[320px]" data-testid={`survey-answers-${l.id}`}>
                    {(() => {
                      // Prefer the new snapshot (Prompt → Answer pairs);
                      // fall back to the legacy {qid: answer} map for
                      // older leads.
                      const snap = l.survey_answers_with_prompts;
                      if (Array.isArray(snap) && snap.length > 0) {
                        const answered = snap.filter((s) => {
                          const a = s.answer;
                          return !(a === null || a === undefined
                                   || (typeof a === "string" && !a.trim())
                                   || (Array.isArray(a) && a.length === 0));
                        });
                        if (answered.length === 0) return <span className="text-muted-foreground">—</span>;
                        const lines = answered.map((s) => {
                          const v = Array.isArray(s.answer) ? s.answer.join(", ") : String(s.answer);
                          return `${s.prompt} → ${v}`;
                        });
                        return (
                          <div className="truncate" title={lines.join("\n")}>
                            {lines.join(" | ")}
                          </div>
                        );
                      }
                      const ans = l.survey_answers || {};
                      const ids = Object.keys(ans);
                      if (ids.length === 0) return <span className="text-muted-foreground">—</span>;
                      const pieces = ids.map((qid) => {
                        const v = ans[qid];
                        return Array.isArray(v) ? v.join(", ") : String(v ?? "");
                      }).filter(Boolean);
                      return (
                        <div className="truncate" title={pieces.join(" | ")}>
                          {pieces.join(" · ")}
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-2 py-0.5"><SocialLinks lead={l} /></td>
                  <td className="px-2 py-0.5 sticky right-0 bg-background z-10">
                    <Link to={`/leads/${l.id}`} className="text-[11px] font-mono text-primary hover:underline" data-testid={`edit-lead-${l.id}`}>Edit →</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <Pager
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={(p) => setPage(p)}
          onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
        />
      </div>
      {showAIDialer && currentCampaignId && (
        <LeadsAIDialerPanel campaignId={currentCampaignId} campaignName={campaign}
                             onClose={() => setShowAIDialer(false)}/>
      )}
    </div>
  );
}

function AssignSelect({ label, users, preferRole, value, onChange, testid }) {
  // If a preferRole list is given, render those users first under a label,
  // then the rest under "Other users", so admin can still pick anyone.
  const preferIds = new Set((preferRole || []).map((u) => u.id));
  const primary = (preferRole || []).filter((u) => preferIds.has(u.id));
  const others = users.filter((u) => !preferIds.has(u.id));
  return (
    <div>
      <label className="meta-label">{label}</label>
      <select
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-sm h-9 mt-1 px-2 border border-input bg-background text-sm"
      >
        <option value="">— Unassigned —</option>
        {primary.length > 0 && (
          <optgroup label={`Suggested (${preferRole?.[0]?.role || ""})`}>
            {primary.map((u) => <option key={u.id} value={u.email}>{u.name} ({u.email})</option>)}
          </optgroup>
        )}
        {others.length > 0 && (
          <optgroup label={primary.length > 0 ? "Other users" : "All users"}>
            {others.map((u) => <option key={u.id} value={u.email}>{u.name} · {u.role} ({u.email})</option>)}
          </optgroup>
        )}
      </select>
    </div>
  );
}

// ───────────────────────────── Pager ──────────────────────────────────────
// 1-indexed pager that matches the user's mock: prev/next + numeric pages
// with "…" gap collapse + a page-size dropdown. Numeric strategy: always
// show page 1 and last, the current page, and 1 neighbour on each side.
// Anything else collapses to "…".
function Pager({ page, pageSize, total, onPageChange, onPageSizeChange }) {
  const totalPages = Math.max(1, Math.ceil((total || 0) / pageSize));
  if (total === 0) return null;
  const first = (page - 1) * pageSize + 1;
  const last = Math.min(page * pageSize, total);

  const buildPages = () => {
    const out = [];
    const add = (n) => out.push(n);
    const gap = "…";
    const seen = new Set();
    const push = (n) => { if (!seen.has(n)) { seen.add(n); add(n); } };
    push(1);
    for (let n = page - 1; n <= page + 1; n++) {
      if (n > 1 && n < totalPages) push(n);
    }
    if (totalPages > 1) push(totalPages);
    // Insert ellipses where there's a gap >1 between consecutive pages.
    const final = [];
    out.sort((a, b) => a - b);
    for (let i = 0; i < out.length; i++) {
      if (i > 0 && out[i] - out[i - 1] > 1) final.push(gap);
      final.push(out[i]);
    }
    return final;
  };

  return (
    <div className="flex items-center justify-between gap-3 flex-wrap text-xs mt-3" data-testid="leads-pager">
      <div className="text-muted-foreground">
        Showing <span className="font-mono font-semibold text-foreground">{first.toLocaleString()}–{last.toLocaleString()}</span> of <span className="font-mono font-semibold text-foreground">{total.toLocaleString()}</span> · Page <span className="font-mono font-semibold text-foreground">{page}</span> of <span className="font-mono font-semibold text-foreground">{totalPages}</span>
      </div>
      <div className="flex items-center gap-1">
        <button
          onClick={() => onPageChange(Math.max(1, page - 1))}
          disabled={page <= 1}
          data-testid="leads-pager-prev"
          className="px-2 py-1 border border-border rounded-sm hover:bg-secondary/40 disabled:opacity-40 disabled:cursor-not-allowed"
        >← Previous</button>
        {buildPages().map((p, i) => p === "…" ? (
          <span key={`gap-${i}`} className="px-2 text-muted-foreground">…</span>
        ) : (
          <button
            key={p}
            onClick={() => onPageChange(p)}
            data-testid={`leads-pager-page-${p}`}
            className={`px-2.5 py-1 border rounded-sm ${p === page ? "bg-primary text-white border-primary font-semibold" : "border-border hover:bg-secondary/40"}`}
          >{p}</button>
        ))}
        <button
          onClick={() => onPageChange(Math.min(totalPages, page + 1))}
          disabled={page >= totalPages}
          data-testid="leads-pager-next"
          className="px-2 py-1 border border-border rounded-sm hover:bg-secondary/40 disabled:opacity-40 disabled:cursor-not-allowed"
        >Next →</button>
        <select
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
          data-testid="leads-pager-size"
          className="ml-2 px-2 py-1 border border-border rounded-sm bg-card"
        >
          {[25, 50, 100, 200].map((n) => <option key={n} value={n}>{n}/page</option>)}
        </select>
      </div>
    </div>
  );
}

